import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, AlertCircle, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EmailAddressInput = ({ addresses = [], onChange, error }) => {
  const [currentEmail, setCurrentEmail] = useState('');
  const [localError, setLocalError] = useState('');

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email cannot be empty';
    if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
    if (addresses.includes(email)) return 'Email address already added';
    return '';
  };

  const handleAddEmail = () => {
    const validationError = validateEmail(currentEmail);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    onChange([...addresses, currentEmail.trim()]);
    setCurrentEmail('');
    setLocalError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    onChange(addresses.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    } else if (e.key === 'Escape') {
      setCurrentEmail('');
      setLocalError('');
    }
  };

  return (
    <div className="space-y-2">
      <div className={cn(
        "p-2 sm:p-3 border rounded-xl bg-gray-50",
        error || localError ? "border-red-300" : "border-gray-200"
      )}>
        {/* Email list */}
        {addresses.length > 0 ? (
          <div className="mb-3 flex flex-wrap gap-1.5 sm:gap-2">
            <AnimatePresence>
              {addresses.map((email, index) => (
                <motion.div
                  key={`${email}-${index}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  className="bg-white border border-gray-200 text-gray-800 text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg flex items-center shadow-sm"
                >
                  <Mail className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-indigo-500 mr-1.5" />
                  <span className="truncate max-w-[200px]">{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(email)}
                    className="ml-1.5 sm:ml-2 text-gray-400 hover:text-gray-600 focus:outline-none p-0.5"
                  >
                    <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex items-center justify-center py-4 text-xs text-gray-400">
            No email addresses added yet
          </div>
        )}

        {/* Input for new email */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="email"
              value={currentEmail}
              onChange={(e) => {
                setCurrentEmail(e.target.value);
                if (localError) setLocalError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter an email address"
              className={cn(
                "w-full border rounded-lg h-9 sm:h-10 px-3 text-xs sm:text-sm focus:outline-none focus:ring-2",
                error || localError 
                  ? "border-red-300 focus:border-red-400 focus:ring-red-200" 
                  : "border-gray-200 focus:border-indigo-400 focus:ring-indigo-200"
              )}
            />
          </div>

          <button
            type="button"
            onClick={handleAddEmail}
            disabled={!currentEmail.trim()}
            className={cn(
              "h-9 sm:h-10 px-3 rounded-lg text-xs sm:text-sm font-medium flex items-center",
              !currentEmail.trim() 
                ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                : "bg-indigo-500 text-white hover:bg-indigo-600"
            )}
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
            Add
          </button>
        </div>

        {/* Error message */}
        <AnimatePresence>
          {(error || localError) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-2 flex items-start text-red-500 text-[11px] sm:text-xs"
            >
              <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 mt-0.5 flex-shrink-0" />
              <span>{error || localError}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default EmailAddressInput; 